import type { BusinessSearchResult } from '../../api/services/lupp/order/dto.order';
import type { DunsBusinessEntity, Lender } from '../../api/services/lupp/quotes/dto.quotes';
import {
  NL_NON_TAXABLE_FEE_CODES,
  NL_STATUTORY_FEE_CODES,
  NL_TAXABLE_FEE_CODES,
} from '../../api/services/lupp/inventory/dto.inventory';
import { parseLocalePrice } from '../../utils/tools';
import { TRADE_IN_SUPPORT_VALUE, CHARGING_VOUCHER_VALUE, QUOTE_FORM_FIELDS } from './constants';

export const roundTo2 = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

const DISCOUNT_INPUT_FIELDS: string[] = [
  QUOTE_FORM_FIELDS.AGENT_DISCOUNT_AMOUNT,
  QUOTE_FORM_FIELDS.AGENT_DISCOUNT_PERCENTAGE,
  QUOTE_FORM_FIELDS.LUCID_DISCOUNT_AMOUNT,
  QUOTE_FORM_FIELDS.LUCID_DISCOUNT_PERCENTAGE,
];

const sumDiscountFields = (values: Partial<Record<string, string | null>>, a: string, b: string) =>
  values[a] || values[b]
    ? roundTo2(parseLocalePrice(values[a]) + parseLocalePrice(values[b])).toFixed(2)
    : '';

/**
 * Sums agent and Lucid inputs into total discount values (cashDiscount / percentageDiscount).
 * Amounts and percentages are summed separately and never converted.
 * @param values - Current form values
 * @returns Total amount and percentage; empty string when no input contributes
 */
export const calculateDiscountTotals = (values: Partial<Record<string, string | null>>) => ({
  cashDiscount: sumDiscountFields(
    values,
    QUOTE_FORM_FIELDS.AGENT_DISCOUNT_AMOUNT,
    QUOTE_FORM_FIELDS.LUCID_DISCOUNT_AMOUNT
  ),
  percentageDiscount: sumDiscountFields(
    values,
    QUOTE_FORM_FIELDS.AGENT_DISCOUNT_PERCENTAGE,
    QUOTE_FORM_FIELDS.LUCID_DISCOUNT_PERCENTAGE
  ),
});

/**
 * Computes the total discount form fields from the agent and Lucid inputs.
 * @param changedField - Name of the edited form field
 * @param values - Current form values
 * @returns Form field updates, or null when the field is not a discount input
 */
export const syncDiscountFields = (
  changedField: string | undefined,
  values: Record<string, string | undefined>
): Record<string, string> | null => {
  if (!changedField || !DISCOUNT_INPUT_FIELDS.includes(changedField)) return null;

  return calculateDiscountTotals(values);
};

type BusinessFieldMap = {
  name: string;
  duns: string;
  address: string;
  postalCode: string;
  city: string;
  country: string;
};

export const getBusinessSelectionFieldValues = (business: BusinessSearchResult | null) => {
  const countryValue = business?.country || business?.countryCode || '';

  return {
    name: business?.name ?? '',
    duns: business?.duns ?? '',
    address: business?.address ?? '',
    postalCode: business?.postalCode ?? '',
    city: business?.city ?? '',
    country: countryValue,
  };
};

export const applyBusinessSelection = (
  business: BusinessSearchResult | null,
  setValue: (name: string, value: unknown) => void,
  fieldMap: BusinessFieldMap
) => {
  const values = getBusinessSelectionFieldValues(business);

  setValue(fieldMap.name, values.name);
  setValue(fieldMap.duns, values.duns);
  setValue(fieldMap.address, values.address);
  setValue(fieldMap.postalCode, values.postalCode);
  setValue(fieldMap.city, values.city);
  setValue(fieldMap.country, values.country);
};

/**
 * Calculates discounted total from base price and discount values
 * @param baseTotal - The original total amount
 * @param cashDiscount - Cash discount value (optional)
 * @param percentageDiscount - Percentage discount value (optional)
 * @returns Discounted total (never negative)
 */
export const calculateDiscountedTotal = (
  baseTotal: number,
  cashDiscount?: string,
  percentageDiscount?: string
): number => {
  const cashDiscountValue = parseLocalePrice(cashDiscount);
  const percentageDiscountValue = parseLocalePrice(percentageDiscount);

  let discountedTotal = baseTotal;
  if (cashDiscountValue > 0) {
    discountedTotal = baseTotal - cashDiscountValue;
  } else if (percentageDiscountValue > 0) {
    discountedTotal = baseTotal - (baseTotal * percentageDiscountValue) / 100;
  }
  return Math.max(0, discountedTotal);
};

/**
 * Resolves the subvention method and DUNS number sent with the selected finance provider.
 * Falls back to the saved quote values when FinOps doesn't return them for that provider.
 * @param financeProvider - Selected finance provider name
 * @param lenders - Lenders returned by FinOps
 * @param saved - Values saved on the quote being edited
 * @returns subventionMethod and dunsNumber, undefined when unknown
 */
export const resolveLenderFields = (
  financeProvider: string | undefined,
  lenders: Lender[],
  saved?: { financeProvider?: string; subventionMethod?: string; dunsNumber?: string }
): { subventionMethod?: string; dunsNumber?: string } => {
  const lender = lenders.find(({ lenderName }) => lenderName === financeProvider);
  const savedValues = financeProvider === saved?.financeProvider ? saved : undefined;
  const lenderDuns = lender?.duns == null ? undefined : String(lender.duns);

  return {
    subventionMethod: lender?.subventionMethod ?? savedValues?.subventionMethod ?? undefined,
    dunsNumber: (lenderDuns ?? savedValues?.dunsNumber) || undefined,
  };
};

/**
 * Builds D&B business entity object with nil checks
 * @param entity - Business search result from D&B API
 * @returns DunsBusinessEntity or undefined if entity is null
 */
export const buildDunsEntity = (
  entity: BusinessSearchResult | null
): DunsBusinessEntity | undefined => {
  if (!entity) return undefined;

  return {
    dunsNumber: entity.duns || '',
    primaryName: entity.primaryName || '',
    companyName: entity.name || '',
    isOutOfBusiness: entity.isOutOfBusiness ?? false,
  };
};

/**
 * Builds D&B business entity from selected entity, or falls back to form DUNS values.
 * This preserves DUNS on quote updates when no new business selection event is fired.
 */
const buildDunsEntityFromSelectionOrFallback = (
  selectedEntity: BusinessSearchResult | null,
  dunsNumber?: string,
  companyName?: string
): DunsBusinessEntity | undefined => {
  const selectedDunsEntity = buildDunsEntity(selectedEntity);
  if (selectedDunsEntity) return selectedDunsEntity;

  if (!dunsNumber) return undefined;

  return {
    dunsNumber,
    primaryName: companyName ?? '',
    companyName: companyName ?? '',
    isOutOfBusiness: false,
  };
};

/**
 * Builds business information section for quote payload
 * @param formData - Form data containing business details
 * @param selectedEntity - Selected business entity from D&B search
 * @returns Business information object or empty object if not Business prospect
 */
export const buildBusinessInfo = (
  formData: {
    prospectType: string;
    companyName?: string;
    companyDuns?: string;
    companyTaxId?: string;
    invoiceAddress?: string;
    businessCity?: string;
    invoiceCountry?: string;
    businessPostalCode?: string;
  },
  selectedEntity: BusinessSearchResult | null
) => {
  if (formData.prospectType !== 'Business') return {};

  const dunsEntity = buildDunsEntityFromSelectionOrFallback(
    selectedEntity,
    formData.companyDuns,
    formData.companyName
  );

  return {
    companyName: formData.companyName,
    ...(formData.companyTaxId ? { companyTaxIdentification: formData.companyTaxId } : {}),
    invoiceAddressLine1: formData.invoiceAddress,
    invoiceCity: formData.businessCity,
    invoiceCountry: formData.invoiceCountry,
    invoicePostalCode: formData.businessPostalCode,
    ...(dunsEntity && { duns: dunsEntity }),
  };
};

/**
 * Builds financing details section for quote payload
 * @param formData - Form data containing finance company details
 * @param selectedEntity - Selected finance company entity from D&B search
 * @returns Financing details object or empty object if not 3rd Party Financing
 */
export const buildFinancingDetails = (
  formData: {
    preferredPaymentMethod: string;
    financeCompanyName?: string;
    financeDuns?: string;
    financeCompanyAddress?: string;
    financeCity?: string;
    financeCountry?: string;
    financePostalCode?: string;
    financeVat?: string;
  },
  selectedEntity: BusinessSearchResult | null
) => {
  if (formData.preferredPaymentMethod !== '3rd Party Financing') return {};

  const dunsEntity = buildDunsEntityFromSelectionOrFallback(
    selectedEntity,
    formData.financeDuns,
    formData.financeCompanyName
  );

  return {
    financingDetails: {
      financeCompanyName: formData.financeCompanyName,
      financeCompanyAddress: formData.financeCompanyAddress,
      financeCity: formData.financeCity,
      financeCountry: formData.financeCountry,
      financePostalCode: formData.financePostalCode,
      financeVat: formData.financeVat,
      ...(dunsEntity && { duns: dunsEntity }),
    },
  };
};

export type TradeInSupportParams = {
  preferredPaymentMethod?: string;
  requestedIncentives?: string[];
};

export const hasTradeInSupportSelected = ({
  preferredPaymentMethod,
  requestedIncentives,
}: TradeInSupportParams): boolean => {
  return (
    preferredPaymentMethod?.toLowerCase() !== 'lfs lease' &&
    (requestedIncentives ?? []).includes(TRADE_IN_SUPPORT_VALUE)
  );
};

export const hasChargingCreditOfferSelected = (
  requestedIncentives?: string[] | undefined
): boolean => {
  return (requestedIncentives ?? []).includes(CHARGING_VOUCHER_VALUE);
};

export const mapRequestedIncentivesToArray = (requestedIncentives?: {
  charging_voucher?: boolean;
  trade_in_support?: boolean;
}): string[] => {
  if (!requestedIncentives) {
    return [];
  }

  const selected: string[] = [];

  if (requestedIncentives.charging_voucher) {
    selected.push(CHARGING_VOUCHER_VALUE);
  }

  if (requestedIncentives.trade_in_support) {
    selected.push(TRADE_IN_SUPPORT_VALUE);
  }

  return selected;
};

export type GrandTotalCalculationParams = TradeInSupportParams & {
  grossSubtotal?: number;
  configurationSubtotal?: number;
  taxRatePercent?: number;
  taxAmount?: number;
  fees?: { value: number; code?: string }[];
  priceAdjustmentValue: number;
  totalCredits?: number;
};

type GrandTotalPricingInput = {
  subtotal?: number;
  configurationSubtotal?: number;
  taxRate?: number;
  taxAmount?: number;
  fees?: { value: number; code?: string }[];
  credits?: { value: number }[];
};

type GrandTotalFormInput = TradeInSupportParams & {
  priceAdjustmentValue: number;
};

export const buildGrandTotalCalculationParams = (
  pricing: GrandTotalPricingInput | undefined,
  formData: GrandTotalFormInput
): GrandTotalCalculationParams => {
  const totalCredits = pricing?.credits?.reduce((sum, credit) => sum + credit.value, 0) ?? 0;

  return {
    grossSubtotal: pricing?.subtotal,
    configurationSubtotal: pricing?.configurationSubtotal,
    taxRatePercent: pricing?.taxRate,
    taxAmount: pricing?.taxAmount,
    fees: pricing?.fees,
    priceAdjustmentValue: formData.priceAdjustmentValue,
    totalCredits,
    preferredPaymentMethod: formData.preferredPaymentMethod,
    requestedIncentives: formData.requestedIncentives,
  };
};

const roundCurrency = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;
const sumFeesByCodes = (
  fees: { value: number; code?: string }[],
  codes: ReadonlySet<string>
): number =>
  fees.reduce((sum, fee) => (fee.code && codes.has(fee.code) ? sum + fee.value : sum), 0);

export type PricingCalculation = {
  subtotalExclVat: number;
  vatAmount: number;
  subtotalWithVat: number;
  grandTotal: number;
};

export const calculatePricing = ({
  grossSubtotal = 0,
  configurationSubtotal,
  taxRatePercent = 0,
  taxAmount,
  fees = [],
  priceAdjustmentValue,
  totalCredits = 0,
}: GrandTotalCalculationParams): PricingCalculation => {
  const vatRate = taxRatePercent / 100;
  const netSubtotal =
    configurationSubtotal ?? (vatRate > 0 ? grossSubtotal / (1 + vatRate) : grossSubtotal);
  const boundedPriceAdjustment = Math.min(
    Math.max(0, priceAdjustmentValue),
    Math.max(0, netSubtotal)
  );
  const discountedSubtotal = netSubtotal - boundedPriceAdjustment;
  // Q4: UNCOMMENT Trade-in Support deduction below to enable for Q4
  // const tradeInSupportDeduction = hasTradeInSupportSelected({
  //   preferredPaymentMethod,
  //   requestedIncentives,
  // })
  //   ? TRADE_IN_SUPPORT_AMOUNT
  //   : 0;
  const tradeInSupportDeduction = 0; // Trade-in Support disabled for Q3
  const discountedSubtotalWithTradeIn = discountedSubtotal - tradeInSupportDeduction;
  const isNlPricing = fees.some((fee) => fee.code && NL_STATUTORY_FEE_CODES.has(fee.code));
  const taxableNlFeeTotal = sumFeesByCodes(fees, NL_TAXABLE_FEE_CODES);
  const nonTaxableNlFeeTotal = sumFeesByCodes(fees, NL_NON_TAXABLE_FEE_CODES);
  const calculatedVat = roundCurrency(
    (discountedSubtotalWithTradeIn + (isNlPricing ? taxableNlFeeTotal : 0)) * vatRate
  );
  const discountTaxReduction = roundCurrency(
    (boundedPriceAdjustment + tradeInSupportDeduction) * vatRate
  );
  const vatAmount =
    isNlPricing && taxAmount !== undefined
      ? roundCurrency(Math.max(0, taxAmount - discountTaxReduction))
      : calculatedVat;

  const subtotalExclVat = roundCurrency(discountedSubtotalWithTradeIn + taxableNlFeeTotal);
  const subtotalWithVat = roundCurrency(subtotalExclVat + vatAmount);
  const grandTotal = isNlPricing
    ? roundCurrency(subtotalWithVat - totalCredits + nonTaxableNlFeeTotal)
    : roundCurrency(subtotalWithVat - totalCredits);

  return { subtotalExclVat, vatAmount, subtotalWithVat, grandTotal };
};

export const calculateGrandTotalWithTradeInSupport = (
  params: GrandTotalCalculationParams
): number => {
  return calculatePricing(params).grandTotal;
};
